import React from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ChevronLeft,
  ChevronRight,
  Stethoscope,
  Baby,
  Heart,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import { getNavItemsForUser, NavItem } from '@/config/sidebarConfig';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { utilizator } = useAuth();
  const { activeSection } = useSectionContext();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [activeItem, setActiveItem] = React.useState<string>('dashboard');

  // Get user level
  const userLevel = utilizator?.user_level?.id || utilizator?.user_level_id || 0;

  // Get navigation items based on user level and active section
  const navItems = getNavItemsForUser(userLevel, activeSection);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      toggleExpanded(item.id);
    } else {
      setActiveItem(item.id);
      if (item.href) {
        // Extract page name from href (e.g., '/patients' -> 'patients')
        const page = item.href.replace(/^\//, '').replace(/\//g, '-') || 'dashboard';
        const navigationEvent = new CustomEvent('navigate', { detail: { page } });
        window.dispatchEvent(navigationEvent);
      }
    }
  };

  const canViewItem = (item: NavItem): boolean => {
    if (!item.requiredLevel) return true;
    if (!utilizator) return false;
    return userLevel >= item.requiredLevel;
  };

  const renderNavItem = (item: NavItem, depth = 0) => {
    if (!canViewItem(item)) return null;

    const Icon = item.icon;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = activeItem === item.id;
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div key={item.id}>
        <Button
          variant={isActive ? 'secondary' : 'ghost'}
          className={cn(
            'w-full justify-start gap-2 px-3',
            depth > 0 && 'ml-4',
            isActive && 'bg-secondary',
            !isOpen && 'justify-center px-2'
          )}
          onClick={() => handleItemClick(item)}
          title={!isOpen ? item.label : undefined}
        >
          <Icon className="h-4 w-4" />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge && (
                <Badge variant="secondary" className="ml-auto">
                  {item.badge}
                </Badge>
              )}
              {hasChildren && (
                <ChevronRight
                  className={cn(
                    'h-4 w-4 transition-transform',
                    isExpanded && 'rotate-90'
                  )}
                />
              )}
            </>
          )}
        </Button>
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1">
            {item.children!.map((child) => renderNavItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get section indicator info
  const getSectionIndicator = () => {
    if (userLevel !== 2 || !activeSection) return null;

    if (activeSection === 'prematuri') {
      return {
        icon: Baby,
        label: 'Prematuri',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
      };
    } else if (activeSection === 'chd') {
      return {
        icon: Heart,
        label: 'MCC',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
      };
    }
    return null;
  };

  const sectionIndicator = getSectionIndicator();

  return (
    <aside
      className={cn(
        'fixed left-0 top-16 z-30 h-[calc(100vh-4rem)] border-r bg-card shadow-sm transition-all duration-300',
        isOpen ? 'w-64' : 'w-16'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Section Indicator for Hospital Users */}
        {sectionIndicator && isOpen && (
          <div className="border-b px-3 py-2">
            <div className={cn('flex items-center gap-2 rounded-md p-2', sectionIndicator.bgColor)}>
              <sectionIndicator.icon className={cn('h-4 w-4', sectionIndicator.color)} />
              <span className={cn('text-sm font-medium', sectionIndicator.color)}>
                Secțiune: {sectionIndicator.label}
              </span>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <ScrollArea className="min-h-0 flex-1 px-2 py-2">
          <nav className="space-y-1">
            {navItems.map((item) => renderNavItem(item))}
          </nav>
        </ScrollArea>

        {/* Footer with Toggle Button */}
        <div className="border-t">
          {isOpen && (
            <div className="p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Stethoscope className="h-3 w-3" />
                <span>Romania</span>
              </div>
            </div>
          )}

          {/* Toggle Button */}
          <div className={cn(
            "flex p-2",
            isOpen ? "justify-end" : "justify-center"
          )}>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8"
              title={isOpen ? "Minimizează meniul" : "Extinde meniul"}
            >
              {isOpen ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
};
